import React from "react";
import { Layout, Card, Typography, Row, Col, Statistic, theme, Skeleton } from "antd";
import {
  UnorderedListOutlined,
  DollarOutlined,
  FileTextOutlined,
  ShopOutlined,
  FileDoneOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { HeaderApp, NavigationBar } from "../../shared/components";
import CompanySwitch from "../../company/components/CompanySwitch";
import { Link } from "react-router-dom";
import { useHybridResponsive } from "../../shared";
import { useFetchQuotesStats } from "../../quotes/hooks/useFetchQuotesStats";
import { useFetchBillsStats } from "../../billing/hooks/useFetchBillsStats";
import SkeletonNode from "../../shared/components/SkeletonNode";


const Dashboard: React.FC = () => {
  const { token } = theme.useToken();
  const { isMobile } = useHybridResponsive();

  const { data: quotesStats = [], isLoading: isLoadingQuotesStats } = useFetchQuotesStats();
  const totalQuotes = quotesStats.length;
  const pendingQuotes = quotesStats.filter((q) => q.status_quote === "pending").length;

  const { data: billsStats = [], isLoading: isLoadingBillsStats } = useFetchBillsStats();
  const totalBills = billsStats.length;
  const pendingBills = billsStats.filter((b) => b.status_bill === "Vigente").length;

  return (
    <>
      <HeaderApp sectionName="Panel de Control" showUserData />

      {isMobile && <CompanySwitch />}

      <Layout.Content style={{ padding: "16px" }} className="max-width-container">
        <Row gutter={12}>
          <Col xs={12} sm={12} md={12} lg={12} xl={12} >
            {isLoadingBillsStats ? <SkeletonNode width="100%" height="116px" /> : (
              <Link to={"/facturas"}>

                <Card size="small" style={{ borderRadius: "12px", height: "100%" }}>
                  <Statistic title="Facturas" value={totalBills} />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      marginTop: "8px",
                    }}
                  >
                    <DollarOutlined
                      style={{ color: token.colorError, fontSize: "14px" }}
                    />
                    <Typography.Text
                      style={{
                        color: token.colorError,
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      {pendingBills} por cobrar
                    </Typography.Text>
                  </div>
                </Card>
              </Link>

            )}
          </Col>
          <Col xs={12} sm={12} md={12} lg={12} xl={12} >
            {isLoadingQuotesStats ? <SkeletonNode width="100%" height="116px" /> : (
              <Link to={"/cotizaciones"}>

                <Card size="small" style={{ borderRadius: "12px" }}>
                  <Statistic title="Cotizaciones" value={totalQuotes} />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      marginTop: "8px",
                    }}
                  >
                    <FileTextOutlined
                      style={{ color: token.colorPrimary, fontSize: "14px" }}
                    />
                    <Typography.Text
                      style={{
                        color: token.colorPrimary,
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      {pendingQuotes} pendientes
                    </Typography.Text>
                  </div>
                </Card>
              </Link>
            )}
          </Col>
        </Row>
        <Typography.Title
          level={3}
          style={{
            margin: "16px 0 8px",
            fontSize: "18px",
            fontWeight: "bold",
            letterSpacing: "-0.015em",
          }}
        >
          Acciones Rápidas
        </Typography.Title>
        <Row gutter={[12, 12]}>
          <Col xs={12}>
            <Link to={"/empresa/dashboard"}>
              <Card
                size="small"
                hoverable
                style={{ borderRadius: "12px", cursor: "pointer" }}
                bodyStyle={{ padding: "16px" }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "40px",
                      height: "40px",
                      borderRadius: "8px",
                      backgroundColor: token.colorPrimaryBg,
                      color: token.colorPrimary,
                    }}
                  >
                    <ShopOutlined style={{ fontSize: "24px" }} />
                  </div>
                  <div>
                    <Typography.Text strong style={{ fontSize: "14px" }}>
                      Empresa
                    </Typography.Text>
                    <br />
                    <Typography.Text
                      style={{ fontSize: "10px", color: token.colorTextSecondary }}
                    >
                      Gestionar datos
                    </Typography.Text>
                  </div>
                </div>
              </Card>
            </Link>
          </Col>
          <Col xs={12}>
            <Link to={"/cotizaciones"}>
              <Card
                size="small"
                hoverable
                style={{ borderRadius: "12px", cursor: "pointer" }}
                bodyStyle={{ padding: "16px" }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "40px",
                      height: "40px",
                      borderRadius: "8px",
                      backgroundColor: token.colorSuccessBg,
                      color: token.colorSuccess,
                    }}
                  >
                    <FileTextOutlined style={{ fontSize: "24px" }} />
                  </div>
                  <div>
                    <Typography.Text strong style={{ fontSize: "14px" }}>
                      Cotización
                    </Typography.Text>
                    <br />
                    <Typography.Text
                      style={{ fontSize: "10px", color: token.colorTextSecondary }}
                    >
                      Crear y enviar
                    </Typography.Text>
                  </div>
                </div>
              </Card>
            </Link>

          </Col>
          <Col xs={12}>
            <Card
              size="small"
              hoverable
              style={{ borderRadius: "12px", cursor: "pointer" }}
              bodyStyle={{ padding: "16px" }}
            >
              <Link to={"/contratos"}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "40px",
                      height: "40px",
                      borderRadius: "8px",
                      backgroundColor: token.colorWarningBg,
                      color: token.colorWarning,
                    }}
                  >
                    <FileDoneOutlined style={{ fontSize: "24px" }} />
                  </div>
                  <div>
                    <Typography.Text strong style={{ fontSize: "14px" }}>
                      Contratos
                    </Typography.Text>
                    <br />
                    <Typography.Text
                      style={{ fontSize: "10px", color: token.colorTextSecondary }}
                    >
                      Revisar firmas
                    </Typography.Text>
                  </div>
                </div>
              </Link>
            </Card>
          </Col>
          <Col xs={12}>
            <Card
              size="small"
              hoverable
              style={{ borderRadius: "12px", cursor: "pointer" }}
              bodyStyle={{ padding: "16px" }}
            >
              <div
                className="disabled"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "40px",
                    height: "40px",
                    borderRadius: "8px",
                    backgroundColor: token.colorErrorBg,
                    color: token.colorError,
                  }}
                >
                  <ShoppingCartOutlined style={{ fontSize: "24px" }} />
                </div>
                <div>
                  <Typography.Text strong style={{ fontSize: "14px" }}>
                    Ordenes
                  </Typography.Text>
                  <br />
                  <Typography.Text
                    style={{ fontSize: "10px", color: token.colorTextSecondary }}
                  >
                    Seguimiento
                  </Typography.Text>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24}>
            <Link to={"/catalogos"}>
              <Card
                size="small"
                hoverable
                style={{ borderRadius: "12px", cursor: "pointer" }}
                bodyStyle={{ padding: "16px" }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "16px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "40px",
                      height: "40px",
                      borderRadius: "8px",
                      backgroundColor: token.colorInfoBg,
                      color: token.colorInfo,
                    }}
                  >
                    <UnorderedListOutlined style={{ fontSize: "24px" }} />
                  </div>
                  <div>
                    <Typography.Text strong style={{ fontSize: "14px" }}>
                      Catálogos
                    </Typography.Text>
                    <br />
                    <Typography.Text
                      style={{ fontSize: "10px", color: token.colorTextSecondary }}
                    >
                      Actualizar inventario y precios
                    </Typography.Text>
                  </div>
                </div>
              </Card>
            </Link>
          </Col>
        </Row>
        <div style={{ height: "128px" }}></div>
      </Layout.Content>

      <NavigationBar activeKey="/dashboard" />
    </>
  );
};

export default Dashboard;
